import React from 'react';

import { bemBlock } from '../../modules/bem';
import { Icon } from '../icon/Icon';

import './TableSortAction.less';

const block = bemBlock('table-sort-action');

const SORT_TRANSITIONS = {
  none: 'asc',
  asc: 'desc',
  desc: 'asc',
} as const;

export type TableSortActionOrder = keyof typeof SORT_TRANSITIONS;

export type TableSortActionChangeParams = {
  sortBy: string | object;
  sortOrder: Exclude<TableSortActionOrder, 'none'>;
  prevSortOrder: TableSortActionOrder;
};

export type TableSortActionProps = {
  sortBy: string | object;
  sortOrder?: TableSortActionOrder;
  onSortChange?: (params?: TableSortActionChangeParams) => void;
};

export const TableSortAction = React.memo(function TableSortAction({
  sortBy,
  sortOrder = 'none',
  onSortChange,
}: TableSortActionProps) {
  const onClick = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onSortChange?.({
        sortBy,
        sortOrder: SORT_TRANSITIONS[sortOrder],
        prevSortOrder: sortOrder,
      });
    },
    [onSortChange, sortBy, sortOrder],
  );

  return (
    <Icon.Stack
      className={block({
        modifiers: {
          disabled: onSortChange === undefined,
        },
        extra: onSortChange ? 'clickable' : undefined,
      })}
      size="sm"
      data-role="sort-action"
      data-selected={sortOrder !== 'none'}
      onClick={onSortChange ? onClick : undefined}
    >
      <Icon
        glyph="caret-up"
        transform={{ x: 0, y: -2 }}
        className={block({
          element: 'arrow',
          modifiers: { selected: sortOrder === 'asc' },
        })}
      />
      <Icon
        glyph="caret-down"
        transform={{ x: 0, y: 6 }}
        className={block({
          element: 'arrow',
          modifiers: { selected: sortOrder === 'desc' },
        })}
      />
    </Icon.Stack>
  );
});
